import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Link } from '@tiptap/extension-link';
import {
  Bold,
  Italic,
  Strikethrough,
  List,
  ListOrdered,
  Link as LinkIcon,
  Undo,
  Redo,
  Heading2,
} from 'lucide-react';
import { normalizeRichTextHtml, isRichTextHtml } from '../../utils/richText';

/** Convert legacy plain-text templates into a single paragraph for Tiptap. */
function toEditorContent(content: string): string {
  const trimmed = (content ?? '').trim();
  if (!trimmed) {
    return '';
  }
  if (isRichTextHtml(trimmed)) {
    return trimmed;
  }
  const escaped = trimmed
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return `<p>${escaped.replace(/\n/g, '<br>')}</p>`;
}

interface EmailTiptapEditorProps {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
  className?: string;
  editorKey?: string;
}

const toolbarButtonStyle = (
  active: boolean
): React.CSSProperties => ({
  backgroundColor: active ? 'var(--color-accent)' : 'transparent',
  color: active ? 'white' : 'var(--color-text-secondary)',
});

export const EmailTiptapEditor: React.FC<EmailTiptapEditorProps> = ({
  content,
  onChange,
  placeholder = 'Write your email body…',
  className = '',
  editorKey,
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2] },
        blockquote: false,
        code: false,
        codeBlock: false,
        horizontalRule: false,
        bulletList: { keepMarks: true, keepAttributes: false },
        orderedList: { keepMarks: true, keepAttributes: false },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          style: 'color: #2563eb; text-decoration: underline;',
        },
      }),
    ],
    content: toEditorContent(content),
    onUpdate: ({ editor: ed }) => {
      onChange(normalizeRichTextHtml(ed.getHTML()));
    },
    editorProps: {
      attributes: {
        class: 'focus:outline-none p-3 tiptap email-template-editor',
        style: `
          min-height: 140px;
          color: var(--color-text-primary);
          background-color: var(--color-bg-primary);
          font-family: Arial, Helvetica, sans-serif;
          font-size: 14px;
          line-height: 1.6;
        `,
      },
    },
  });

  useEffect(() => {
    if (!editor) {
      return;
    }
    const next = toEditorContent(content);
    const current = editor.getHTML();
    if (next !== current) {
      editor.commands.setContent(next, { emitUpdate: false });
    }
  }, [editor, content, editorKey]);

  if (!editor) {
    return null;
  }

  const addLink = () => {
    const previous = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Link URL (https://…)', previous || 'https://');
    if (url === null) {
      return;
    }
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const ToolButton: React.FC<{
    title: string;
    active?: boolean;
    onClick: () => void;
    children: React.ReactNode;
  }> = ({ title, active = false, onClick, children }) => (
    <button
      type="button"
      title={title}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className="p-1.5 rounded transition-colors"
      style={toolbarButtonStyle(active)}
      onMouseEnter={(e) => {
        if (!active) {
          e.currentTarget.style.backgroundColor = 'var(--color-bg-primary)';
          e.currentTarget.style.color = 'var(--color-text-primary)';
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.color = 'var(--color-text-secondary)';
        }
      }}
    >
      {children}
    </button>
  );

  return (
    <div
      className={`border rounded-lg ${className}`}
      style={{
        borderColor: 'var(--color-border)',
        backgroundColor: 'var(--color-bg-secondary)',
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div
        className="border-b p-2 flex flex-wrap items-center gap-1 rounded-t-lg"
        style={{
          borderColor: 'var(--color-border)',
          backgroundColor: 'var(--color-bg-tertiary)',
        }}
      >
        <ToolButton
          title="Bold"
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold className="w-4 h-4" />
        </ToolButton>
        <ToolButton
          title="Italic"
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic className="w-4 h-4" />
        </ToolButton>
        <ToolButton
          title="Strikethrough"
          active={editor.isActive('strike')}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          <Strikethrough className="w-4 h-4" />
        </ToolButton>

        <div className="w-px h-5 mx-1" style={{ backgroundColor: 'var(--color-border)' }} />

        <ToolButton
          title="Heading"
          active={editor.isActive('heading', { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        >
          <Heading2 className="w-4 h-4" />
        </ToolButton>
        <ToolButton
          title="Bullet list"
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <List className="w-4 h-4" />
        </ToolButton>
        <ToolButton
          title="Numbered list"
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered className="w-4 h-4" />
        </ToolButton>
        <ToolButton title="Link" active={editor.isActive('link')} onClick={addLink}>
          <LinkIcon className="w-4 h-4" />
        </ToolButton>

        <div className="w-px h-5 mx-1" style={{ backgroundColor: 'var(--color-border)' }} />

        <ToolButton
          title="Undo"
          onClick={() => editor.chain().focus().undo().run()}
        >
          <Undo className="w-4 h-4" />
        </ToolButton>
        <ToolButton
          title="Redo"
          onClick={() => editor.chain().focus().redo().run()}
        >
          <Redo className="w-4 h-4" />
        </ToolButton>
      </div>

      <div className="relative">
        <EditorContent editor={editor} />
        {editor.isEmpty && (
          <p
            className="absolute top-3 left-3 pointer-events-none text-sm"
            style={{ color: 'var(--color-text-muted)' }}
          >
            {placeholder}
          </p>
        )}
      </div>

      <p className="px-3 py-2 text-xs border-t" style={{ color: 'var(--color-text-muted)', borderColor: 'var(--color-border)' }}>
        Email-safe formatting only (bold, lists, links). Avoid tables and images for Gmail/Outlook compatibility.
      </p>
    </div>
  );
};
